import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X, Smartphone, Share, CheckCircle2 } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;

  useEffect(() => {
    // Don't show if already installed or previously dismissed
    if (isStandalone) return;
    const dismissedAt = localStorage.getItem('install-prompt-dismissed');
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return; // Don't show again for 7 days
    }

    // Chrome/Android: Listen for the beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show after a short delay (don't interrupt first impression)
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // iOS: Show guide after delay if not installed
    if (isIos) {
      setTimeout(() => setShowIosGuide(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIosGuide(false);
    setDismissed(true);
    localStorage.setItem('install-prompt-dismissed', Date.now().toString());
  };

  // Already installed or dismissed
  if (isStandalone || dismissed) return null;

  // Chrome/Android install banner
  if (showBanner && deferredPrompt) {
    return (
      <div className="install-prompt-overlay">
        <div className="install-prompt">
          <button className="install-prompt-close" onClick={handleDismiss} aria-label="Close">
            <X size={18} />
          </button>

          <div className="install-prompt-icon">
            <Smartphone size={32} />
          </div>

          <h3 className="install-prompt-title">{t('install_title')}</h3>
          <p className="install-prompt-desc">{t('install_desc')}</p>

          <div className="install-prompt-features">
            <span>
              <CheckCircle2 size={14} className="text-green-500" /> {t('install_feature_offline')}
            </span>
            <span>
              <CheckCircle2 size={14} className="text-green-500" /> {t('install_feature_fast')}
            </span>
            <span>
              <CheckCircle2 size={14} className="text-green-500" /> {t('install_feature_push')}
            </span>
          </div>

          <button className="install-prompt-btn" onClick={handleInstall}>
            <Download size={18} />
            {t('install_button')}
          </button>

          <button className="install-prompt-later" onClick={handleDismiss}>
            {t('install_later')}
          </button>
        </div>
      </div>
    );
  }

  // iOS install guide
  if (showIosGuide && isIos) {
    return (
      <div className="install-prompt-overlay">
        <div className="install-prompt">
          <button className="install-prompt-close" onClick={handleDismiss} aria-label="Close">
            <X size={18} />
          </button>

          <div className="install-prompt-icon">
            <Smartphone size={32} />
          </div>

          <h3 className="install-prompt-title">{t('install_title')}</h3>
          <p className="install-prompt-desc">{t('install_ios_desc')}</p>

          <div className="install-prompt-ios-steps">
            <div className="ios-step">
              <span className="ios-step-num">1</span>
              <span>{t('install_ios_step1')}</span>
              <Share size={16} style={{ marginLeft: 4 }} />
            </div>
            <div className="ios-step">
              <span className="ios-step-num">2</span>
              <span>{t('install_ios_step2')}</span>
            </div>
            <div className="ios-step">
              <span className="ios-step-num">3</span>
              <span>{t('install_ios_step3')}</span>
            </div>
          </div>

          <button className="install-prompt-later" onClick={handleDismiss}>
            {t('install_understood')}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
